import { Stack, Typography } from '@prenuvo/halo';

import { CwfActiveDocument } from '@/api';
import { StudyResult } from '@/shared/model';

import { determineDocumentPlaceholders } from '../../../../../util';
import { PatientDocLink } from '../../patient-docs-side-panel.model';
import { usePatientDocs } from '../use-patient-docs.hook';
import { DocumentRow } from './document-row.component';
import { DocumentRowContainer } from './document-row-container.component';
import { DocumentsByStudyHeader } from './documents-by-study-header.component';
import { MedicalHistorySummaryRow } from './medical-history-summary-row.component';
import { RadiologyReportRow } from './radiology-report-row.component';

interface DocumentsByStudyProps {
  documents: CwfActiveDocument[];
  mhsLink?: PatientDocLink;
  radiologyReportLink?: PatientDocLink;
  study?: StudyResult;
}

export function DocumentsByStudy({
  documents,
  mhsLink,
  radiologyReportLink,
  study,
}: DocumentsByStudyProps) {
  const { types } = usePatientDocs();

  const show = determineDocumentPlaceholders({
    documents,
    servicesInfo: study?.poetryMetadata?.servicesInfo ?? [],
    sku: study?.skuId,
    types,
  });

  return (
    <Stack className="w-full rounded-lg border border-neutral-600 p-2" gap={0} variant="vertical">
      <DocumentsByStudyHeader
        poetryMetadata={study?.poetryMetadata}
        scanDate={study?.date}
        sku={study?.skuId}
        studyId={study?.id}
        timezone={study?.timezone}
      />

      {radiologyReportLink && <RadiologyReportRow radiologyReportLink={radiologyReportLink} />}

      {mhsLink && <MedicalHistorySummaryRow mhsLink={mhsLink} />}

      {show.advancedNeuroPlaceholder && (
        <DocumentRowContainer>
          <Typography
            className="py-2 italic dark:text-neutral-400"
            translationKey="FCT.PATIENT_DOCS.SIDE_PANEL.DOCUMENT_TYPES.PLACEHOLDER.BRAIN_HEALTH_ASSESSMENT"
            variant="paragraph-sm"
          />
        </DocumentRowContainer>
      )}

      {show.advancedHeartPlaceholder && (
        <DocumentRowContainer>
          <Typography
            className="py-2 italic dark:text-neutral-400"
            translationKey="FCT.PATIENT_DOCS.SIDE_PANEL.DOCUMENT_TYPES.PLACEHOLDER.HEART_HEALTH_ASSESSMENT"
            variant="paragraph-sm"
          />
        </DocumentRowContainer>
      )}

      {show.bodyCompPlaceholder && (
        <DocumentRowContainer>
          <Typography
            className="py-2 italic dark:text-neutral-400"
            translationKey="FCT.PATIENT_DOCS.SIDE_PANEL.DOCUMENT_TYPES.PLACEHOLDER.BODY_COMPOSITION"
            variant="paragraph-sm"
          />
        </DocumentRowContainer>
      )}

      {documents.map((document) => (
        <DocumentRow key={document.uuid} document={document} />
      ))}
    </Stack>
  );
}
